import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ukStamp } from "@/lib/time";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Loader2, CheckCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { ChatAttachButton, ChatAttachment } from "./ChatAttach";
import { ConnieMark } from "@/components/connie/ConnieMark";
import { ConnieText } from "@/components/connie/ConnieText";

const OK_EXT = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx", "csv"];

/**
 * ChatWidget — floating in-house messaging bubble for the lender/introducer portals.
 * Polls /hub/chat/me while open (4.5s) and /hub/chat/unread while closed (20s); both
 * responses carry `desk`, so the launcher can show whether anyone is actually there.
 *
 * It stands down on the Messages page, the way the Hub's dock does: that page *is* this
 * thread full size, and floating a second copy over it covers the page's own send button
 * and leaves two chat surfaces arguing on one screen.
 *
 * On a phone the launcher sits above the bottom tab row rather than on top of it.
 */
export const ChatWidget = ({ portalLabel = "Partner portal" }) => {
  const { pathname } = useLocation();
  const onThreadPage = pathname.endsWith("/messages") || pathname.endsWith("/connie");  // never on top of a chat
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [desk, setDesk] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [pending, setPending] = useState(null);
  const scrollRef = useRef(null);
  const attachRef = useRef(null);
  const dragDepth = useRef(0);
  const openRef = useRef(false);
  openRef.current = open;

  const fetchThread = useCallback(async () => {
    try {
      const r = await api.get("/hub/chat/me?mark_read=1");
      setMessages(r.data.messages || []);
      if (r.data.desk) setDesk(r.data.desk);
      setUnread(0);
      setLoaded(true);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
    }
  }, []);

  // Any page can ask the desk to open — the right-rail "Message the desk" buttons use this.
  useEffect(() => {
    const onOpen = () => { setNudge(false); setOpen(true); };
    window.addEventListener("portal-chat:open", onOpen);
    return () => window.removeEventListener("portal-chat:open", onOpen);
  }, []);

  useEffect(() => {
    if (disabled) return;
    let t;
    const tick = async () => {
      if (!openRef.current) {
        try {
          const r = await api.get("/hub/chat/unread");
          setUnread(r.data.unread || 0);
          if (r.data.desk) setDesk(r.data.desk);
        } catch (e) { if (e?.response?.status === 403) { setDisabled(true); return; } }
      }
      t = setTimeout(tick, 20000);
    };
    tick();
    return () => clearTimeout(t);
  }, [disabled]);

  // One quiet introduction per session — the desk says hello, then gets out of the way.
  useEffect(() => {
    if (disabled || !desk || sessionStorage.getItem("cx_desk_hello")) return;
    const show = setTimeout(() => {
      if (openRef.current) return;
      sessionStorage.setItem("cx_desk_hello", "1");
      setNudge(true);
    }, 6500);
    const hide = setTimeout(() => setNudge(false), 20000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [disabled, desk]);

  const dismissNudge = () => { setNudge(false); sessionStorage.setItem("cx_desk_hello", "1"); };

  // A file dropped anywhere on the dock (or on the launcher) goes straight into the
  // attach panel — you still say which case it belongs to, you just skip the paperclip.
  const takeDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!OK_EXT.includes(ext)) {
      toast.error(`We can't file a .${ext} — send a PDF, image, Word, Excel or CSV`);
      return;
    }
    dismissNudge();
    if (open && attachRef.current) attachRef.current.openWith(file);
    else { setPending(file); setOpen(true); }
  };

  const dragIn = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const dragOut = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragging(false);
  };
  const dragOver = (e) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); };

  useEffect(() => {
    if (open && pending && attachRef.current) {
      attachRef.current.openWith(pending);
      setPending(null);
    }
  }, [open, pending]);

  useEffect(() => {
    if (!open || disabled) return;
    let t;
    const tick = async () => { await fetchThread(); t = setTimeout(tick, 4500); };
    tick();
    return () => clearTimeout(t);
  }, [open, disabled, fetchThread]);

  const stick = useRef(true);
  useEffect(() => { if (open) stick.current = true; }, [open]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages, open]);
  const onScroll = (e) => {
    const el = e.currentTarget;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    stick.current = true;
    setSending(true);
    try {
      await api.post("/hub/chat/me/messages", { body });
      setInput("");
      await fetchThread();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  if (disabled || onThreadPage) return null;

  const staffed = !!desk?.staffed;
  const deskLine = desk
    ? `${desk.reply}${desk.hours ? ` · open ${desk.hours}` : ""}`
    : "Direct line to your Connexis team";
  const pillText = dragging
    ? "Drop it here and we'll file it on a case"
    : unread > 0
      ? `You have ${unread > 1 ? `${unread} replies` : "a reply"} from the desk`
      : desk?.reply || "Message the Connexis desk";

  return (
    <>
      {open && (
        <div className="fixed bottom-[152px] right-4 z-[70] flex h-[600px] max-h-[66vh] w-[420px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 duration-200 animate-in fade-in slide-in-from-bottom-4 sm:right-6 lg:bottom-24 lg:max-h-[78vh]"
             onDragEnter={dragIn} onDragOver={dragOver} onDragLeave={dragOut} onDrop={takeDrop}
             data-testid="portal-chat-panel">
          {/* Header */}
          <div className="relative bg-[#0B1220] text-white px-5 py-4 shrink-0 overflow-hidden">
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
            <div className="absolute -top-10 -right-6 w-32 h-32 rounded-full bg-[#0055FF]/30 blur-2xl pointer-events-none" />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="relative flex size-2">
                    {staffed && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />}
                    <span className={`relative inline-flex rounded-full size-2 ${staffed ? "bg-teal-400" : desk?.office_hours ? "bg-[#4d8bff]" : "bg-slate-500"}`} />
                  </span>
                  <div className="text-[14px] font-semibold tracking-[-0.01em]">Connexis desk</div>
                </div>
                <div className="mt-1 text-[11.5px] text-white/55" data-testid="portal-chat-desk-status">
                  {deskLine}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-white/10 text-white/70" data-testid="portal-chat-close">
                <X className="size-4" />
              </button>
            </div>
          </div>

          {dragging && (
            <div className="absolute inset-0 z-20 grid place-items-center bg-white/92 backdrop-blur-sm" data-testid="portal-chat-dropzone">
              <div className="mx-6 w-full rounded-2xl border-2 border-dashed border-[#0055FF]/45 bg-[#0055FF]/[0.04] px-6 py-10 text-center">
                <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-[#0055FF]/10 text-[#0055FF]">
                  <Upload className="size-5" />
                </span>
                <div className="mt-3 text-[14px] font-semibold text-slate-900">Drop it here</div>
                <div className="mt-1 text-[12px] text-slate-500">
                  Tell us which case it&apos;s for and we&apos;ll file it on the deal, not just in the chat.
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50" data-testid="portal-chat-messages">
            {!loaded ? (
              <div className="grid place-items-center h-full text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
            ) : messages.length === 0 ? (
              <div className="grid place-items-center h-full text-center px-6">
                <div>
                  <div className="mx-auto grid place-items-center size-11 rounded-2xl bg-[#0055FF]/[0.08] border border-[#0055FF]/20 text-[#0055FF]"><MessageCircle className="size-5" /></div>
                  <div className="mt-3 text-[13.5px] font-semibold text-slate-800">Start a conversation</div>
                  <div className="mt-1 text-[12px] text-slate-500 leading-relaxed">Ask about a deal, your commission, documents — anything. The Connexis team sees it instantly.</div>
                </div>
              </div>
            ) : (
              messages.map((m) => <Bubble key={m.id} msg={m} />)
            )}
          </div>

          {/* Composer */}
          <div className="relative border-t border-slate-200 bg-white p-3 flex items-end gap-2 shrink-0">
            <ChatAttachButton ref={attachRef} casesPath="/hub/chat/me/cases" uploadPath="/hub/chat/me/attachments"
                              onSent={fetchThread} testPrefix="portal-chat-attach" />
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder="Write a message…"
              className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0055FF]/50 focus:bg-white transition-colors max-h-28"
              data-testid="portal-chat-input"
            />
            <button onClick={send} disabled={sending || !input.trim()}
                    className="grid place-items-center size-10 rounded-xl bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                    data-testid="portal-chat-send">
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            </button>
          </div>
        </div>
      )}

      {/* Launcher — a desk with someone behind it, not a help icon */}
      <div
        className="group fixed bottom-[86px] right-4 z-[70] flex items-center justify-end gap-2.5 sm:right-6 lg:bottom-5"
        onDragEnter={dragIn} onDragOver={dragOver} onDragLeave={dragOut} onDrop={takeDrop}
        data-testid="portal-chat-launcher-zone"
      >
        <div
          className={`desk-pill hidden max-w-[19rem] items-center gap-2.5 rounded-full border border-slate-200/90 bg-white/95 py-2.5 pl-4 pr-3 shadow-[0_18px_44px_-20px_rgba(11,18,32,0.5)] backdrop-blur transition-all duration-300 sm:flex ${
            nudge || dragging
              ? "pointer-events-auto translate-x-0 opacity-100"
              : "pointer-events-none translate-x-3 opacity-0 group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100"
          } ${open ? "!hidden" : ""}`}
          data-testid="portal-chat-nudge"
        >
          <span className="relative flex size-2 shrink-0">
            {staffed && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-70" />}
            <span className={`relative inline-flex size-2 rounded-full ${staffed ? "bg-teal-500" : desk?.office_hours ? "bg-[#0055FF]" : "bg-slate-400"}`} />
          </span>
          <span className="min-w-0">
            <span className="block text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-400">Connexis desk</span>
            <span className="block truncate text-[12.5px] font-medium text-slate-800">{pillText}</span>
          </span>
          {nudge && !dragging && (
            <button onClick={dismissNudge} className="ml-1 shrink-0 rounded-full p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500"
                    aria-label="Dismiss" data-testid="portal-chat-nudge-dismiss">
              <X className="size-3" />
            </button>
          )}
        </div>

        <button
          onClick={() => { dismissNudge(); setOpen((v) => !v); }}
          className={`relative grid size-14 place-items-center rounded-full border border-white/10 bg-[#0B1220] text-white shadow-xl shadow-slate-900/30 transition-transform duration-200 hover:scale-105 active:scale-95 ${
            dragging ? "scale-110 ring-4 ring-[#0055FF]/35" : ""
          }`}
          data-testid="portal-chat-launcher"
          aria-label={`Chat with Connexis — ${portalLabel}`}
        >
          {!open && (
            <>
              <span className={`desk-halo pointer-events-none absolute -inset-1.5 rounded-full ${
                staffed ? "bg-teal-400/25" : "bg-[#0055FF]/25"}`} />
              {unread > 0 && <span className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-[#0055FF]/40" />}
              <span className="desk-orbit pointer-events-none absolute inset-0 overflow-hidden rounded-full">
                <span className="absolute left-1/2 top-0 h-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-white/60 to-transparent" />
              </span>
            </>
          )}
          <span className="relative">
            {open ? <X className="size-5" /> : <MessageCircle className={`size-5 ${nudge ? "desk-nudge" : ""}`} />}
          </span>
          {!open && (
            <span className={`pointer-events-none absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full border-2 border-[#0B1220] ${
              staffed ? "bg-teal-400" : desk?.office_hours ? "bg-[#4d8bff]" : "bg-slate-500"}`}
                  data-testid="portal-chat-presence-dot" />
          )}
          {unread > 0 && !open && (
            <span className="absolute -top-1 -right-1 grid place-items-center min-w-5 h-5 px-1 rounded-full bg-[#0055FF] text-white text-[10px] font-bold border-2 border-white" data-testid="portal-chat-unread">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </div>
    </>
  );
};

function Bubble({ msg }) {
  if (msg.sender === "system") return <SystemReceipt msg={msg} />;
  if (msg.sender === "connie") return <ConnieReply msg={msg} />;
  const mine = msg.sender === "party";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`portal-chat-msg-${msg.id}`}>
      <div className={`max-w-[80%] flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}>
        {!mine && (
          <div className="grid place-items-center size-6 rounded-full bg-[#0B1220] text-white text-[9px] font-bold shrink-0 mb-0.5">C</div>
        )}
        <div>
          <div className={`rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
            mine ? "bg-[#0055FF] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
          }`}>
            {msg.body}
            <ChatAttachment att={msg.attachment} mine={mine} />
          </div>
          <div className={`mt-1 text-[10px] text-slate-400 ${mine ? "text-right" : ""}`}>{fmtTime(msg.created_at)}{VIA[msg.via] ? ` · ${VIA[msg.via]}` : ""}</div>
        </div>
      </div>
    </div>
  );
}

function SystemReceipt({ msg }) {
  return (
    <div className="flex justify-center" data-testid={`portal-chat-receipt-${msg.id}`}>
      <div className="max-w-[88%] rounded-xl border border-teal-200 bg-teal-50/80 px-3.5 py-2.5 text-center">
        <div className="flex items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-teal-700">
          <CheckCheck className="size-3" /> Received
        </div>
        <div className="mt-1.5 text-[12px] leading-relaxed text-teal-900/85">{msg.body}</div>
        <div className="mt-1.5 text-[10px] text-teal-700/60">Automatic receipt · {fmtTime(msg.created_at)}</div>
      </div>
    </div>
  );
}

const fmtTime = ukStamp;

const VIA = { email: "by email", sms: "by text" };

function ConnieReply({ msg }) {
  return (
    <div className="flex justify-start" data-testid={`portal-chat-connie-${msg.id}`}>
      <div className="max-w-[84%] flex items-start gap-2">
        <ConnieMark size={24} orbit={false} testId={`portal-chat-connie-mark-${msg.id}`} />
        <div>
          <div className="mb-1 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#0044CC]">Connie · Connexis</div>
          <div className="connie-bubble break-words text-[13px]"><ConnieText text={msg.body} /></div>
          <div className="mt-1 text-[10px] text-slate-400">
            {msg.handed ? "A person on the desk will pick this up too · " : ""}{fmtTime(msg.created_at)}{VIA[msg.via] ? ` · sent ${VIA[msg.via]}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
